import type { ValueEvent } from '@adornis/base/utilTypes.js';
import { getByID } from '@adornis/baseql/operations/mongo.js';
import { User } from '@adornis/buchungsheld-db/entities/user.js';
import { ParticipationState } from '@adornis/buchungsheld-db/enums/enums.js';
import '@adornis/buchungsheld-theme/components/tr-chip.js';
import { TrDialog } from '@adornis/buchungsheld-theme/components/tr-dialog.js';
import '@adornis/buchungsheld-theme/components/tr-icon.js';
import '@adornis/buchungsheld-theme/fonts/fonts.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { css } from '@adornis/chemistry/directives/css.js';
import '@adornis/chemistry/elements/components/x-hr.js';
import { currentUser } from '@adornis/users/client/currentUser.js';
import { html } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { filter, map, switchMap } from 'rxjs/operators';

@customElement('bh-prompt-generate-global-certificate')
export class BHPromptGenerateGlobalCertificate extends TrDialog<string[] | undefined> {
  static override get element_name() {
    return 'bh-prompt-generate-global-certificate';
  }

  @state() private readonly user = new RXController(this, currentUser<User>());
  @state() private readonly participations = new RXController(
    this,
    this.user.observable.pipe(
      filter(Boolean),
      switchMap(user => getByID(User, user._id)({ participations: { _id: 1, state: 1, product: { name: 1 } } })),
      map(user => user?.participations.filter(p => p.state === ParticipationState.PARTICIPATED)),
      filter(Boolean),
    ),
  );

  @state() private readonly selectedParticipations = {} as Record<string, boolean>;

  get participationIDs() {
    return Object.entries(this.selectedParticipations)
      .filter(([id, selected]) => selected)
      .map(([id]) => id);
  }

  override content() {
    if (!this.participations.value) return html` <tr-loading-state></tr-loading-state> `;
    const participations = this.participations.value;

    return html`
      <tr-flex space="md">
        <tr-h3 bold>${this._translation.translate('PROFILE', 'generate_global_certificate')}</tr-h3>
        <table>
          <tr>
            <th ${css({ textAlign: 'left' })}>${this._translation.translate('workshop', 'singular')}</th>
            <th></th>
          </tr>
          ${participations.map((participation, index) => {
            return html`
              <tr>
                <td>${participation.product!.name}</td>
                <td>
                  <tr-checkbox
                    outlined
                    boolean-only
                    enableicon="adjust"
                    disableicon
                    @value-picked=${(e: ValueEvent<boolean>) => {
                      this.selectedParticipations[participation._id] = !this.selectedParticipations[participation._id];
                      this.requestUpdate();
                    }}
                    .value=${!!this.selectedParticipations[participation._id]}
                  ></tr-checkbox>
                </td>
              </tr>
            `;
          })}
        </table>

        <br />

        ${[
          {
            text: this._translation.translate('GENERATE'),
            mode: 'action',
            action: () => this.close(this.participationIDs),
            disabled: this.participationIDs.length === 0,
          },
          {
            text: this._translation.translate('CANCEL'),
            mode: 'outline',
            action: () => this.close(undefined),
          },
        ].map(({ text, mode, action, disabled }) => {
          return html`
            <tr-button mode=${mode} @click=${action} ?disabled=${disabled}> <x-text>${text}</x-text> </tr-button>
          `;
        })}
      </tr-flex>
    `;
  }
}
